import { Activity, ShieldAlert } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getRiskAssessment } from '../lib/riskAssessment.mjs';
import type { DashboardData } from '../types';

interface Props {
  stats: DashboardData['stats'];
  refreshing: boolean;
}

export default function CommandCenter({ stats, refreshing }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const assessment = getRiskAssessment({
    articles: stats.articles,
    highThreat: stats.highThreat,
    conflict: stats.conflict
  });
  const transition = prefersReducedMotion ? { duration: 0 } : { duration: 0.65, ease: 'easeOut' as const };

  return (
    <motion.section
      className="command-center"
      aria-label="Command Center"
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition}
    >
      <div className="command-center-heading">
        <span>COMMAND CENTER</span>
        <motion.i
          className={`command-live-dot ${refreshing ? 'is-refreshing' : ''}`}
          animate={prefersReducedMotion ? undefined : { opacity: [0.45, 1, 0.45], scale: [0.92, 1.08, 0.92] }}
          transition={prefersReducedMotion ? undefined : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden="true"
        />
      </div>

      <div className="defcon-card">
        <div className="command-card-topline"><span><ShieldAlert size={13} /> OPEN-SOURCE DEFCON</span><span className={`risk-band band-${assessment.band.toLowerCase()}`}>{assessment.band}</span></div>
        <div className="defcon-value-row">
          <span className="defcon-label">LEVEL</span>
          <AnimatePresence mode="wait" initial={false}>
            <motion.strong
              key={assessment.defcon}
              className={`defcon-value defcon-${assessment.defcon}`}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 5, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, y: -5, scale: 1.04 }}
              transition={transition}
              aria-label={`Estimated level ${assessment.defcon}`}
            >
              {assessment.defcon}
            </motion.strong>
          </AnimatePresence>
          <span className="defcon-scale">{assessment.defcon} / 5<br /><small>ESTIMATE</small></span>
        </div>
        <div className="defcon-track" role="img" aria-label={`Open-source estimate level ${assessment.defcon} out of 5`}>
          <motion.span
            className={`defcon-fill defcon-fill-${assessment.defcon}`}
            initial={{ width: 0 }}
            animate={{ width: `${(assessment.defcon / 5) * 100}%` }}
            transition={transition}
          />
        </div>
        <p className="command-caveat">NEWS-BASED INDICATOR · NOT OFFICIAL DEFCON</p>
      </div>

      <div className="risk-meter-card">
        <div className="command-card-topline"><span><Activity size={13} /> GLOBAL RISK METER</span><span className="risk-score-value">{assessment.score}<small> / 100</small></span></div>
        <div className="risk-meter-track" role="meter" aria-label="Global open-source risk meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={assessment.score}>
          <motion.span
            className={`risk-meter-fill band-${assessment.band.toLowerCase()}`}
            initial={{ width: 0 }}
            animate={{ width: `${assessment.score}%` }}
            transition={transition}
          />
        </div>
        <div className="risk-meter-foot"><span>{assessment.band} SIGNAL</span><span>{stats.highThreat} HIGH / CRITICAL · {stats.conflict} CONFLICT / MILITARY</span></div>
      </div>
      <p className="risk-formula-note">Score = clamp(round(800 × [0.7H + 0.3C] / max(N, 1)), 0–100). 20-point bands map DEFCON 5→1; public-source estimate only.</p>
    </motion.section>
  );
}
